/**
 * US Phone Number Utilities
 * Provides formatting and validation functions for US phone numbers
 */

/**
 * Format US phone number as (XXX) XXX-XXXX
 * Automatically formats input as user types
 * @param value - Raw input value (can contain digits, spaces, hyphens, parentheses)
 * @returns Formatted phone number string in format (XXX) XXX-XXXX
 */
export const formatUSPhone = (value?: string | null): string => {
  if (!value) {
    return ''
  }
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, '')

  // Limit to 10 digits (US phone numbers)
  const limitedDigits = digitsOnly.slice(0, 10)

  // Format based on length
  if (limitedDigits.length === 0) {
    return ''
  } else if (limitedDigits.length <= 3) {
    return `(${limitedDigits}`
  } else if (limitedDigits.length <= 6) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`
  } else {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`
  }
}

/**
 * Validate US phone number
 * Validates that the phone number:
 * - Contains exactly 10 digits
 * - Area code (first 3 digits) cannot start with 0 or 1
 * - Exchange code (next 3 digits) cannot start with 0 or 1
 * 
 * @param phone - Phone number string (can be formatted or unformatted)
 * @returns true if valid US phone number, false otherwise
 */
export const validateUSPhone = (phone?: string | null): boolean => {
  if (!phone) {
    return false
  }
  const digitsOnly = phone.replace(/\D/g, '')
  
  // Must be exactly 10 digits
  if (digitsOnly.length !== 10) {
    return false
  }
  
  // Area code and exchange cannot start with 0 or 1
  // Format: [2-9]XX [2-9]XX XXXX
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(digitsOnly)
}

/**
 * Get validation error message for US phone number
 * @param phone - Phone number string to validate
 * @returns Error message string, or empty string if valid
 */
export const getUSPhoneErrorMessage = (phone?: string | null): string => {
  if (!phone || !phone.trim()) {
    return 'Phone number is required'
  }

  const digitsOnly = phone.replace(/\D/g, '')
  
  if (digitsOnly.length < 10) {
    return 'Please enter a complete 10-digit US phone number'
  }
  
  if (!validateUSPhone(phone)) {
    return 'Please enter a valid US phone number (area code and exchange cannot start with 0 or 1)'
  }
  
  return ''
}

/**
 * Extract digits only from phone number
 * Useful for storing phone numbers in database or API calls
 * @param phone - Formatted or unformatted phone number
 * @returns 10-digit string containing only digits
 */
export const getPhoneDigitsOnly = (phone: string): string => {
  return phone.replace(/\D/g, '').slice(0, 10)
}

